import {
  Box,
  Button,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import InfoBadge, { OriginEnum } from "../../../common/info-badge/InfoBadge";
import { useLangContext } from "../../../../context/langContext";
import { useProtectedAxiosInstance } from "../../../../hooks/axiosInstance";
import { useEffect, useState } from "react";
import { dateFormatter } from "../../../../utils/date-formatter";
import BroadCastingMedSectionComponent from "./BroadCastingMedSec";
import { useNotificationContext } from "../../../../context/notificationContext";
import errorResolver from "../../../../utils/error-resolver";
import { useNavigate } from "react-router-dom";
import { VoiceBroadcastDetailsSchema } from "../../../../services/Schemas/Broadcast";
import {
  BroadcastingDetailsRequest,
  BroadcastingReportListColumnFilterable,
  VoiceBroadCastReportSchema,
} from "../../../../services/Endpoints/broadcast";
import { IUserLink } from "../../common/UserLink";
import BroadCastingTabsComponent from "./BroadCastingTable";
import BroadcastCardInfo from "./BroadcastCardInfo";
import { FilterOperatorEnum } from "../../../../services/Schemas/ListQueryString";
import MuiLinearProgress from "../../common/MuiLinearProgress";
import {
  CallOutgoing,
  CallRemove,
  CloseCircle,
  MoreCircle,
  UserSquare,
} from "iconsax-react";

interface IBroadcastDetailsBoardProps {
  id: string;
  setCreator: (payload: IUserLink["user"]) => void;
  setStatus: (payload: string) => void;
}

const infos = {
  en: {
    title: "عنوان کمپین",
    ticketNumber: "کد پیگیری",
    created_at: "تاریخ ایجاد",
  },
  fa: {
    title: "عنوان کمپین",
    ticketNumber: "کد پیگیری",
    created_at: "تاریخ ایجاد",
  },
};

const detailList = [
  { id: "1", Icon: UserSquare, color: "#0088FF" },
  {
    id: "2",
    Icon: MoreCircle,
    color: "#FFA200",
  },
  { id: "3", Icon: CallOutgoing, color: "#3C9A4F" },
  {
    id: "4",
    Icon: CallRemove,
    color: "#FC4D55",
  },
];
const failed = [
  "failed",
  "blocked",
  "no_answer",
  "busy",
  "rejected",
  "unreachable",
  "device_off",
  "invalid_number",
  "canceled",
  "retry",
];

const handleDetail = (id: string, reports: VoiceBroadCastReportSchema[]) => {
  const totalCount = reports
    .map((itm) => itm.total)
    .reduce((prev, curr) => prev + curr, 0);
  if (id == "1") {
    return ` ${totalCount} شماره `;
  } else if (id == "2") {
    const currCount = reports
      .map((itm) =>
        ![...failed, "answered"].includes(itm.status) ? itm.total : 0,
      )
      .reduce((prev, curr) => prev + curr, 0);
    const percentage = ((currCount / totalCount) * 100).toFixed(0);
    return `${currCount} تماس در صف ارسال (${percentage}%)`;
  } else if (id == "3") {
    const currCount = reports
      .map((itm) => (itm.status == "answered" ? itm.total : 0))
      .reduce((prev, curr) => prev + curr, 0);
    const percentage = ((currCount / totalCount) * 100).toFixed(0);
    return ` ${currCount} تماس موفق (${percentage}%) `;
  } else {
    const currCount = reports
      .map((itm) => (failed.includes(itm.status) ? itm.total : 0))
      .reduce((prev, curr) => prev + curr, 0);
    const percentage = ((currCount / totalCount) * 100).toFixed(0);
    return ` ${currCount} تماس ناموفق (${percentage}%) `;
  }
};

export default function BroadCastingDetailsSectionComponent({
  id,
  setCreator,
  setStatus,
}: Readonly<IBroadcastDetailsBoardProps>) {
  const { lang } = useLangContext();
  const [data, setData] = useState<VoiceBroadcastDetailsSchema | undefined>(
    undefined,
  );

  const [reports, setReports] = useState<VoiceBroadCastReportSchema[] | null>(
    null,
  );
  const [reload, setReload] = useState(false);
  const navigate = useNavigate();
  const [timeFilter, setTimeFilter] = useState<string[] | null>(null);

  const [waiting, setWaiting] = useState(true);
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();

  const getData = async () => {
    try {
      setData(undefined);
      setWaiting(true);
      const broadcastingDetailsRequest = new BroadcastingDetailsRequest(
        protectedInstance,
      );
      const { data } = await broadcastingDetailsRequest.send({
        broadcastingNumber: id,
      });
      setData(data.voice_broadcast);
      setStatus(data.voice_broadcast.status);
      setCreator(data.voice_broadcast.user);
      const { data: _data } = await broadcastingDetailsRequest.report(
        {
          broadcastingNumber: id,
        },
        {
          filters: timeFilter
            ? [
                {
                  column: BroadcastingReportListColumnFilterable.UPDATED_AT,
                  operator: FilterOperatorEnum.BETWEEN,
                  value: timeFilter,
                },
              ]
            : [],
        },
      );
      setReports(_data.number_reports);
      setWaiting(false);
    } catch (error: any) {
      errorResolver({ error, setNotification });
      navigate("/404");
      console.error(error.message);
    }
  };

  useEffect(() => {
    getData();
  }, [reload, timeFilter]);

  return (
    <Box
      sx={{
        width: "100%",
        typography: "body1",
        borderRadius: "6px",
        height: "fit-content",
        position: "relative",
      }}
    >
      {waiting && <MuiLinearProgress />}
      <Stack
        direction="column"
        justifyContent="space-around"
        sx={{
          backgroundColor: "#ffffff",
          borderColor: (theme) => theme.palette.others.borderColor,
          borderWidth: "1px",
          borderStyle: "solid",
          borderRadius: "4px",
          px: "18px",
          py: "16px",
        }}
      >
        <Stack
          direction={{ xs: "column-reverse", lg: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "space-between", lg: "start" }}
          mx="18px"
          gap="10px"
        >
          <Stack
            direction="row"
            alignItems={{ xs: "space-between", lg: "start" }}
            justifyContent="space-between"
            gap="12px"
          >
            <Typography
              variant="h6MediumBold"
              color={(theme) => theme.palette.dominant.main}
              sx={{
                display: "flex",
                gap: "5px",
              }}
            >
              {data && <>{infos[lang].title}:</>}
              <Typography
                variant="h6MediumBold"
                color={(thm) => thm.palette.dominant.main}
              >
                {data ? (
                  <>{data?.caption}</>
                ) : (
                  <Skeleton width="200px" height="30px" variant="rectangular" />
                )}
              </Typography>
            </Typography>
            {data ? (
              <>
                <InfoBadge
                  origin={OriginEnum.BROADCASTING_STATUS}
                  status={data?.status as "pending"}
                  mx="0"
                />
              </>
            ) : (
              <Skeleton width="200px" height="30px" variant="rounded" />
            )}
          </Stack>
          <Stack direction="row-reverse" alignItems="center" gap="4px">
            <Stack
              direction="row"
              justifyContent="space-between"
              width="100%"
              gap="3px"
            >
              <Typography
                variant="h6MediumBold"
                color={(theme) => theme.palette.dominant.main}
              >
                {data ? (
                  <>{infos[lang].created_at}:</>
                ) : (
                  <Skeleton width="100px" height="30px" variant="rectangular" />
                )}
              </Typography>
              <Typography
                variant="h6MediumBold"
                color={(theme) => theme.palette.dominant.main}
                dir="ltr"
              >
                {data ? (
                  <>{dateFormatter({ lang, date: data.created_at })}</>
                ) : (
                  <Skeleton width="100px" height="30px" variant="rectangular" />
                )}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      {!waiting ? (
        <Stack
          direction="row"
          gap={{ xs: "3px", lg: "32px" }}
          justifyContent="space-between"
          flexWrap={{ xs: "wrap", lg: "nowrap" }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              py: { xs: "3px", lg: "20px" },
            }}
            width={{ xs: "100%", lg: "100%" }}
            flexWrap="wrap"
            gap="4px"
          >
            {detailList.map((itm) => (
              <Stack width={{ xs: "100%", lg: "24%" }}>
                <BroadcastCardInfo
                  Icon={itm.Icon}
                  color={itm.color}
                  key={itm.id}
                  subTitle={handleDetail(itm.id, reports!)}
                />
              </Stack>
            ))}
          </Stack>
        </Stack>
      ) : (
        <Stack
          direction="row"
          gap={{ xs: "3px", lg: "32px" }}
          justifyContent="space-between"
          flexWrap={{ xs: "wrap", lg: "nowrap" }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              py: { xs: "3px", lg: "20px" },
            }}
            width={{ xs: "100%", lg: "100%" }}
            flexWrap="wrap"
            gap="4px"
          >
            {detailList.map((itm) => (
              <Stack width={{ xs: "100%", lg: "24%" }}>
                <Skeleton variant="rectangular" width="" height="70px" />
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
      <Stack gap="28px" minHeight={"65vh"}>
        <BroadCastingMedSectionComponent
          broadcastingInfo={data}
          initialReports={reports}
          loading={waiting}
        />
        <BroadCastingTabsComponent broadcastId={id} type={data?.type} />
      </Stack>
    </Box>
  );
}
